import * as React from "react";
import { FormEvent, useMemo } from "react";
import {
	CommonFormContainer,
	CommonFormDescription,
	CommonFormRightSide,
	CommonFormRightSideFooter,
	CommonFormWrapper,
} from "../../Modals/CommonSignForm/CommonSignForm.styled";
import { Button, InputField } from "../../../common";
import { ButtonTheme, NOTIFICATION_TYPE } from "../../../../constants";
import { observer } from "mobx-react";
import { useStores } from "../../../../stores";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { gtmEvent } from "../../../../helpers";
import { SignInSchema } from "../../../../validation-schems";
import { FacebookClientResponsive } from "../../../../response";
import { useTranslation } from "react-i18next";
import { Description, Title } from "../../../../pages/ContactPage/ContactPage.styled";
import { createDeferredValidationHandlers } from "../../../../helpers/formik";

interface SignInFormProps {}

export const SignInForm = observer(({}: SignInFormProps) => {
	const { t } = useTranslation();

	const { authStore, notificationsStore, geoPluginStore } = useStores();
	const navigate = useNavigate();

	const formik = useFormik({
		validationSchema: SignInSchema,
		validateOnBlur: true,
		validateOnChange: false,
		initialValues: {
			email: "",
			password: "",
		},
		onSubmit: async (values) => {
			try {
				await authStore.signIn(values);
				await authStore.check();
				authStore.signInForm.modal.close();
				navigate("/cabinet/my-day");

				notificationsStore.add({
					title: "Success",
				});

				gtmEvent({ event: "Sign_in", email: values.email.toLowerCase() });

				gtmEvent({ event: "User_ID", email: authStore.userStore.id });
			} catch (e: any) {
				notificationsStore.add({
					type: NOTIFICATION_TYPE.ERROR,
					title: "Error",
					message: e.errors.join("<br/>"),
				});
			}
		},
	});

	const handleOnForgoPassword = (e: FormEvent) => {
		e.preventDefault();
		navigate("/sign/forgot-password");
	};

	const handleOnSignUp = (e: FormEvent) => {
		e.preventDefault();
		navigate("/sign");
	};

	const handleOnFacebook = async (data: FacebookClientResponsive) => {
		try {
			await authStore.signFacebook(data);
			authStore.signInForm.modal.close();
			navigate("/cabinet/my-day");

			notificationsStore.add({
				title: "Success",
			});
		} catch {}
	};

	const handleOnGoogle = async (data: any) => {
		try {
			await authStore.signGoogle({
				access_token: data.accessToken,
				email: data.profileObj.email,
				first_name: data.profileObj.givenName,
				last_name: data.profileObj.familyName,
				picture: data.profileObj.imageUrl,
			});
			authStore.signInForm.modal.close();
			navigate("/cabinet/my-day");

			gtmEvent({ event: "Sign_in", email: data.profileObj.email.toLowerCase() });
		} catch {}
	};

	const emailField = useMemo(() => createDeferredValidationHandlers(formik, "email"), [formik]);
	const passwordField = useMemo(() => createDeferredValidationHandlers(formik, "password"), [formik]);

	return (
		<>
			<Title dangerouslySetInnerHTML={{ __html: t("pages.sign.in.title") }} />
			<div style={{ height: 30 }} />
			<Description
				dangerouslySetInnerHTML={{
					__html: t("pages.sign.description"),
				}}
			/>

			<CommonFormContainer onlyForm>
				<CommonFormRightSide>
					<CommonFormWrapper
						onSubmit={(e) => {
							formik.handleSubmit(e);
						}}
						onKeyDown={(e) => {
							e.stopPropagation();

							if (e.key === "Enter") {
								e.preventDefault();
								formik.handleSubmit();
							}
						}}
					>
						<InputField
							type={"email"}
							name={"email"}
							value={formik.values.email}
							placeholder={"Your email"}
							errorMessage={emailField.getError()}
							onChange={emailField.handleChange}
							onBlur={emailField.handleBlur}
						/>
						<InputField
							type={"password"}
							name={"password"}
							value={formik.values.password}
							placeholder={"Your password"}
							errorMessage={passwordField.getError()}
							onChange={passwordField.handleChange}
							onBlur={passwordField.handleBlur}
						/>
						<Button theme={ButtonTheme.LINK} onClick={handleOnForgoPassword}>
							{t("messages.forgot")}
						</Button>
						<Button type={"submit"} disabled={!formik.values.email || !formik.values.password || !formik.isValid}>
							{t("buttons.sign_in")}
						</Button>
					</CommonFormWrapper>

					<CommonFormRightSideFooter>
						<CommonFormDescription>{t("messages.not_account")}</CommonFormDescription>
						<Button theme={ButtonTheme.LINK_LARGE} onClick={handleOnSignUp}>
							{t("buttons.sign_up")}
						</Button>
					</CommonFormRightSideFooter>
				</CommonFormRightSide>
			</CommonFormContainer>
		</>
	);
});
